import { memo } from 'react'

import { Box, Button, Group, Stack, Text, Title } from '@mantine/core'

import {
  TokenAndTextRender,
  TokenRender,
} from '@repo/common/components/TokenRender'
import { usdFormatter } from '@repo/common/helpers/formatters'
import { countFormatter } from '@repo/common/helpers/formatters'
import {
  type PositionsType,
  useClosePosition,
} from '@repo/common/queries/fission_dex'
import type { FundSymbol } from '@repo/common/queries/products'

import { ActiveLabel } from './ActiveLabel'

export const LiquidityPositionRow = memo<{
  position: PositionsType['positions'][number]
  fund_symbol: FundSymbol
  // eslint-disable-next-line max-lines-per-function
}>(function LiquidityPositionRow({ position, fund_symbol }) {
  const send = useClosePosition({ fund_symbol })

  return (
    <Group gap="xl" justify="flex-start">
      <Group gap="xs" wrap="nowrap">
        <TokenRender
          type="lp"
          size="var(--mantine-font-size-xl1)"
          symbol={[fund_symbol, 'USDC']}
        />
        <Stack gap="0">
          <Text style={{ textWrap: 'nowrap' }}>USDC ⇄ {fund_symbol}</Text>
          <Text opacity={0.7}>1.0% fee tier</Text>
        </Stack>
      </Group>
      <Stack gap="xs" align="flex-start" miw="100px">
        {position.in_range && <ActiveLabel label="In Range" />}
      </Stack>
      <Box flex="1 1" />
      <Stack gap="0" align="self-start">
        <Title order={2} fz="xs" lh="2">
          Selected Range
        </Title>
        <Text style={{ textWrap: 'nowrap' }} lh="var(--mantine-font-size-xl1)">
          {usdFormatter(position.price_low)} -{' '}
          {usdFormatter(position.price_high)}
        </Text>
      </Stack>
      <Stack gap="0" align="self-start">
        <Title order={2} fz="xs" lh="2">
          My Liquidity
        </Title>
        <Group gap="xs" wrap="nowrap" h="var(--mantine-font-size-xl1)">
          <TokenAndTextRender size="md" symbol="USDC">
            {countFormatter(position.usdc_pool_tokens, {
              maximumFractionDigits: 4,
            })}
          </TokenAndTextRender>
          <TokenAndTextRender size="md" symbol={fund_symbol}>
            {countFormatter(position.fund_pool_tokens, {
              maximumFractionDigits: 4,
            })}
          </TokenAndTextRender>
        </Group>
      </Stack>
      <Stack gap="0" align="self-start">
        <Title order={2} fz="xs" lh="2" style={{ textWrap: 'nowrap' }}>
          Total Fees Earned
        </Title>
        <Group gap="xs" wrap="nowrap" h="var(--mantine-font-size-xl1)">
          <TokenAndTextRender size="md" symbol="USDC">
            {countFormatter(position.usdc_rewards, {
              maximumFractionDigits: 4,
            })}
          </TokenAndTextRender>

          <TokenAndTextRender size="md" symbol={fund_symbol}>
            {countFormatter(position.fund_rewards, {
              maximumFractionDigits: 4,
            })}
          </TokenAndTextRender>
        </Group>
      </Stack>
      <Stack>
        <Button
          variant="subtle"
          loading={send.isPending}
          disabled={!send.isReady}
          size="sm"
          onClick={() => send.mutate({ position_id: position.id })}
        >
          Close Position
        </Button>
      </Stack>
    </Group>
  )
})
